import React, { ReactNode, useEffect } from 'react';
import { Navigation } from '../Navigation/Navigation';
import { Footer } from '../Footer/Footer';
import { CookieConsent } from '../CookieConsent/CookieConsent';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { usePageReady } from '../../contexts/PageReadyContext';
import {
  setDocumentTitle,
  updateMetaTag,
  updateOgTags,
  updateCanonicalUrl,
  clearPageSeo,
} from '@shared/seo/meta';
import { injectJsonLd, removeJsonLd } from '@shared/seo/jsonLd';
import styles from './Layout.module.css';

interface LayoutProps {
  children: ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { settings, isLoading } = useSiteSettings();
  const { pageReady } = usePageReady();
  const notReady = isLoading || !pageReady;

  // Signal to the build-time prerender (scripts/prerender/run.js) that the
  // current route is fully populated: site settings loaded AND the page's
  // data fetch resolved. Puppeteer waits on this flag before snapshotting.
  useEffect(() => {
    const ready =
      !!settings.siteName && settings.siteName !== 'Rezerwacje Online' && pageReady;
    (window as unknown as { __PRERENDER_READY__?: boolean }).__PRERENDER_READY__ = ready;
  }, [settings.siteName, pageReady]);

  // Set page title, meta tags, OG tags, and canonical URL
  useEffect(() => {
    if (!settings.siteName || settings.siteName === 'Rezerwacje Online') return;

    setDocumentTitle(settings.siteName);

    const siteUrl = window.location.origin;
    const pageUrl = siteUrl + window.location.pathname;
    const description =
      settings.seoDescription ||
      `Zarezerwuj wizytę w ${settings.siteName} - rezerwacje online`;

    updateMetaTag('name', 'description', description);
    updateCanonicalUrl(pageUrl);
    updateOgTags({
      title: settings.siteName,
      description,
      url: pageUrl,
      type: 'website',
      locale: 'pl_PL',
      image: settings.ogImage?.url || settings.logo?.url,
      siteName: settings.siteName,
    });

    return () => {
      clearPageSeo();
    };
  }, [settings.siteName, settings.seoDescription, settings.ogImage?.url, settings.logo?.url]);

  // Inject JSON-LD structured data
  useEffect(() => {
    if (!settings.siteName || settings.siteName === 'Rezerwacje Online') return;

    const siteUrl = window.location.origin;

    const schemaType = settings.businessType || 'HealthAndBeautyBusiness';

    const organizationLd: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': schemaType,
      '@id': `${siteUrl}/#organization`,
      name: settings.siteName,
      url: siteUrl,
      areaServed: { '@type': 'Country', name: 'Poland' },
      potentialAction: {
        '@type': 'ReserveAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${siteUrl}/rezerwacja`,
          actionPlatform: [
            'http://schema.org/DesktopWebPlatform',
            'http://schema.org/MobileWebPlatform',
          ],
        },
        result: { '@type': 'Reservation', name: 'Rezerwacja wizyty' },
      },
    };

    if (settings.phone) organizationLd.telephone = settings.phone;
    if (settings.email) organizationLd.email = settings.email;
    if (settings.address) {
      organizationLd.address = {
        '@type': 'PostalAddress',
        streetAddress: settings.address,
        addressCountry: 'PL',
      };
    }

    const sameAs = [settings.facebookUrl, settings.instagramUrl].filter(Boolean);
    if (sameAs.length > 0) organizationLd.sameAs = sameAs;

    const websiteLd = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      name: settings.siteName,
      url: siteUrl,
      inLanguage: 'pl-PL',
      publisher: { '@id': `${siteUrl}/#organization` },
    };

    injectJsonLd('organization', organizationLd);
    injectJsonLd('website', websiteLd);

    return () => {
      removeJsonLd('organization');
      removeJsonLd('website');
    };
  }, [
    settings.siteName,
    settings.phone,
    settings.email,
    settings.address,
    settings.facebookUrl,
    settings.instagramUrl,
  ]);

  const loadingScreen = (
    <div className={styles.loadingScreen}>
      <div className={styles.brandLogo}>
        <svg className={styles.brandIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <div className={styles.brandText}>
          Umów<span className={styles.brandTextAccent}>Się</span>
        </div>
      </div>
      <div className={styles.progressContainer}>
        <div className={styles.progressBar} />
      </div>
    </div>
  );

  // While settings load, children aren't mounted yet — show loader alone.
  if (isLoading) return loadingScreen;

  // Settings loaded: children (page components) mount and start fetching. We
  // hide the full layout with display:none until the page signals ready —
  // this keeps effects firing but prevents any visible layout shifts. Once
  // pageReady=true, the loader is removed and the layout is shown in its
  // final laid-out state in a single paint.
  return (
    <>
      {notReady && loadingScreen}
      <div
        className={styles.layout}
        style={notReady ? { display: 'none' } : undefined}
        aria-hidden={notReady || undefined}
      >
        <a href="#main-content" className={styles.skipLink}>Przejdź do treści</a>
        <Navigation />
        <main id="main-content" tabIndex={-1} className={styles.main}>{children}</main>
        <Footer />
        <CookieConsent />
      </div>
    </>
  );
};
